<script setup lang="ts">
import { computed } from 'vue'

import EBadge from './EBadge.vue'
import EIcon from './EIcon.vue'
import './base.css'
import { changesSince, type ChangeEntry, type ChangesSinceOptions } from '../lib/changes'
import { formatDate } from '../lib/format'
import { interpolate } from '../lib/strings'

/**
 * "What changed since I last acted on this, and who changed it" (XFN-UX-015),
 * for the right rail of a document screen.
 *
 * It sits above the full audit timeline rather than replacing it. The timeline
 * answers "what has ever happened here" and is complete; this answers "what do I
 * need to read before I act again", which is a different and much shorter list —
 * and the one the user actually came back for.
 *
 * The baseline is derived, not stored: the viewer's own most recent *act* in the
 * audit log (`lib/changes.ts` explains why an opened tab is not an act). Where
 * there is no baseline the component says so plainly instead of marking
 * everything new — including the case where the fetched window may simply be too
 * short to contain the user's last action, which is not the same as never having
 * acted and is not reported as if it were.
 */

const props = withDefaults(
  defineProps<{
    entries: readonly ChangeEntry[]
    options?: ChangesSinceOptions
    /** Renders an actor id as a name when the entry carries no label. */
    actorName?: (id: string) => string
    /** Renders a field key as its business label. */
    fieldName?: (field: string) => string
    /** IANA zone; business dates are read in the tenant's timezone (§5). */
    timeZone?: string
    /** `{count}`, `{since}` */
    headingLabel?: string
    upToDateLabel?: string
    neverActedLabel?: string
    unknownBaselineLabel?: string
    /** `{count}` */
    moreLabel?: string
    emptyValueLabel?: string
    /** Spoken between the old and new value; the arrow is decorative. */
    changedToLabel?: string
    /** Accessible name for the region. */
    label?: string
    /** Field rows shown before "and N more". */
    maxFields?: number
  }>(),
  {
    headingLabel: '{count} changes since you last acted, on {since}',
    upToDateLabel: 'Nothing has changed since you last acted on this.',
    neverActedLabel: 'You have not acted on this record yet.',
    unknownBaselineLabel:
      'Your last action on this record is older than the history loaded here — load more to see what changed.',
    moreLabel: 'and {count} more',
    emptyValueLabel: 'empty',
    changedToLabel: 'changed to',
    label: 'Changes since you last acted',
    maxFields: 6,
  },
)

const result = computed(() => changesSince(props.entries, props.options ?? {}))

function sinceText(at: string | undefined): string {
  if (at === undefined) return ''
  // `exactOptionalPropertyTypes` is on: an absent timezone is an absent key,
  // not a key holding undefined.
  return props.timeZone === undefined
    ? formatDate(at, { preset: 'dateTime' })
    : formatDate(at, { preset: 'dateTime', timeZone: props.timeZone })
}

const heading = computed(() => {
  const value = result.value
  if (value.baseline === 'unknown') return props.unknownBaselineLabel
  if (value.baseline === 'none') return props.neverActedLabel
  if (value.count === 0) return props.upToDateLabel
  return interpolate(props.headingLabel, { count: value.count, since: sinceText(value.markerAt) })
})

const tone = computed(() => (result.value.count > 0 ? 'accent' : 'neutral'))

const shownFields = computed(() => result.value.fields.slice(0, props.maxFields))
const hiddenFieldCount = computed(() => Math.max(0, result.value.fields.length - props.maxFields))

const actors = computed(() =>
  result.value.actors.map((actor) => actor.label ?? props.actorName?.(actor.id) ?? actor.id),
)

function fieldLabel(field: string): string {
  return props.fieldName?.(field) ?? field
}

/** Audit values are already masked where sensitive (§5); this only formats. */
function display(value: unknown): string {
  if (value === null || value === undefined || value === '') return props.emptyValueLabel
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  try {
    return JSON.stringify(value)
  } catch {
    return props.emptyValueLabel
  }
}
</script>

<template>
  <section class="e-changes" :aria-label="label">
    <header class="e-changes__head">
      <EIcon :name="result.count > 0 ? 'refresh' : 'success'" :size="14" aria-hidden="true" />
      <p class="e-changes__heading" aria-live="polite">{{ heading }}</p>
      <EBadge v-if="result.count > 0" :tone="tone" size="sm">{{ result.count }}</EBadge>
    </header>

    <p v-if="actors.length > 0" class="e-changes__actors">{{ actors.join(', ') }}</p>

    <dl v-if="shownFields.length > 0" class="e-changes__fields">
      <template v-for="change in shownFields" :key="change.field">
        <dt class="e-changes__field">{{ fieldLabel(change.field) }}</dt>
        <dd class="e-changes__values">
          <span class="e-changes__from">{{ display(change.from) }}</span>
          <span class="e-visually-hidden">{{ changedToLabel }}</span>
          <EIcon name="arrowRight" :size="12" aria-hidden="true" class="e-changes__arrow" />
          <span class="e-changes__to">{{ display(change.to) }}</span>
        </dd>
      </template>
    </dl>

    <p v-if="hiddenFieldCount > 0" class="e-changes__more">
      {{ interpolate(moreLabel, { count: hiddenFieldCount }) }}
    </p>

    <slot :result="result" />
  </section>
</template>

<style>
.e-changes {
  display: flex;
  flex-direction: column;
  gap: var(--erpx-space-2);
  padding: var(--erpx-space-3);
  border: 1px solid var(--erpx-color-border-subtle);
  border-radius: var(--erpx-radius);
  background-color: var(--erpx-color-surface-sunken);
}

.e-changes__head {
  display: flex;
  align-items: center;
  gap: var(--erpx-space-2);
  color: var(--erpx-color-text-secondary);
}

.e-changes__heading {
  flex: 1;
  margin: 0;
  color: var(--erpx-color-text-primary);
  font-size: var(--erpx-text-sm);
  font-weight: var(--erpx-weight-medium);
  line-height: var(--erpx-leading-tight);
}

.e-changes__actors {
  margin: 0;
  color: var(--erpx-color-text-muted);
  font-size: var(--erpx-text-xs);
}

.e-changes__fields {
  display: grid;
  grid-template-columns: minmax(6rem, auto) 1fr;
  gap: 2px var(--erpx-space-2);
  margin: 0;
  font-size: var(--erpx-text-xs);
}

.e-changes__field {
  color: var(--erpx-color-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.e-changes__values {
  display: flex;
  align-items: center;
  gap: var(--erpx-space-1);
  margin: 0;
  min-width: 0;
  color: var(--erpx-color-text-primary);
  font-variant-numeric: tabular-nums;
}

.e-changes__from {
  color: var(--erpx-color-text-muted);
  text-decoration: line-through;
}

.e-changes__arrow {
  flex: none;
  color: var(--erpx-color-text-muted);
}

.e-changes__to {
  font-weight: var(--erpx-weight-medium);
}

.e-changes__more {
  margin: 0;
  color: var(--erpx-color-text-muted);
  font-size: var(--erpx-text-xs);
}
</style>
